import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { message } from 'antd';

// =====================================================================
// 契约适配层
// =====================================================================
//
// 背景：本文件原先按「`{code, message, data}` 信封 + camelCase 字段 + 另一套路径」
// 编写，但后端 FastAPI 实际返回的是：
//   1) 无信封的裸响应（列表为 {items, total, page, page_size}）
//   2) snake_case 字段（knowledge_points / marks / total_marks / parent_id …）
//   3) 另一套接口路径（/api/agents/* 而非 /agent/*，/api/classes/{id}/analytics 而非
//      /analytics/class-overview 等）
// 结果是 17 个页面全部取到 undefined，即使接口 200 也渲染不出数据。
//
// 修复策略：**在这一层做双向适配，而不是去改 17 个页面**。
//   - 请求方向：camelCase 参数/载荷 → snake_case
//   - 响应方向：裸响应 → {code, message, data} 信封；列表页 → {list, total, page, pageSize}
//              实体字段 → 前端类型期望的名字
// 这样页面的既有写法（res.data.list / row.knowledgePoints / …）无需改动即可工作。

// ===================== 类型定义（前端视角） =====================

export interface Question {
  id: string;
  title: string;
  content: string;
  answer: string;
  analysis: string;
  type: string;
  difficulty: 'easy' | 'medium' | 'hard';
  score: number;
  knowledgePoints: string[];
  subject: string;
  grade: string;
  tags: string[];
  /** 考纲标签，如 2022新课标 / 中考高频 */
  examTags?: string[];
  /** 易错点 */
  errorPronePoints?: string[];
  /** 重难点 */
  keyPoints?: string[];
  createdAt: string;
  updatedAt: string;
  [key: string]: any;
}

export interface Paper {
  id: string;
  title: string;
  subject: string;
  grade: string;
  duration: number;
  totalScore: number;
  status: 'draft' | 'published' | 'archived';
  questions: PaperQuestion[];
  createdAt: string;
  updatedAt: string;
  [key: string]: any;
}

export interface PaperQuestion {
  questionId: string;
  order: number;
  score: number;
  section: string;
  [key: string]: any;
}

export interface KnowledgeNode {
  id: string;
  name: string;
  parentId: string | null;
  level: number;
  description?: string;
  children?: KnowledgeNode[];
  [key: string]: any;
}

export interface Courseware {
  id: string;
  title: string;
  subject: string;
  grade: string;
  textbookVersion?: string;
  type: string;
  fileUrl: string;
  fileSize: number;
  createdAt: string;
  /** 后端落库的文件类型：pptx / docx / pdf / edupdf … */
  fileType?: string;
  /** active=正文已抽取可用；failed=抽取失败，原因见 parseError */
  status?: string;
  /** 正文抽取失败原因（如 .edupdf 未配置解密口令）；成功时为 null */
  parseError?: string | null;
  [key: string]: any;
}

export interface LessonPlanGenerationTask {
  taskId: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  progress: number;
  stage: string;
  lessonPlanId?: string | number | null;
  error?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface LessonPlan {
  id: string;
  title: string;
  subject: string;
  grade: string;
  objectives: string;
  process: string;
  blackboard: string;
  content: string;
  createdAt: string;
  updatedAt: string;
  [key: string]: any;
}

export interface Mistake {
  id: string;
  studentId: string;
  studentName: string;
  questionId: string;
  questionTitle: string;
  subject: string;
  knowledgePoint: string;
  errorType: string;
  errorCount: number;
  createdAt: string;
  [key: string]: any;
}

export interface ApiResponse<T = any> {
  code: number;
  message: string;
  data: T;
}

export interface PageResult<T = any> {
  list: T[];
  total: number;
  page: number;
  pageSize: number;
}

// ===================== 题库来源 / 去重类型 =====================

export interface SourceItem {
  value: string;
  label: string;
  color: string;
  description: string;
  default_status: string;
  count: number;
  ratio: number;
}

export interface QuestionSourceStats {
  total: number;
  sources: SourceItem[];
  unclassified: { grade: number };
}

export interface QuestionFacet {
  value: string | number;
  count: number;
}

export interface QuestionFacets {
  total: number;
  statuses: QuestionFacet[];
  formats: QuestionFacet[];
  difficulties: QuestionFacet[];
  textbookVersions: QuestionFacet[];
  regions: QuestionFacet[];
  years: QuestionFacet[];
}

export interface DedupCheckItem {
  index: number;
  content: string;
  duplicated: boolean;
  matched_id: number | null;
  similarity: number;
  reason: string;
}

export interface DedupCheckResult {
  total: number;
  duplicated_count: number;
  items: DedupCheckItem[];
}

// ===================== 知识点提取类型 =====================

export interface KnowledgeExtraction {
  courseware_id: number;
  courseware_title: string;
  tree: any[]; // 嵌套建议树：章节为一级节点，知识点为子节点
  /** 结果可能不完整（分段数超上限，或有分段提取失败） */
  truncated: boolean;
  /**
   * 正文被切成的分段总数。
   * 注意：后端不做 camelCase 转换，返回的就是 snake_case（`chunks_total`），
   * 之前这里写成 chunksTotal，页面上一直取到 undefined。
   */
  chunks_total: number;
  /** 成功提取并参与归并的分段数 */
  chunks_used: number;
}

export interface ApplyExtractionResult {
  created_count: number;
  skipped_count: number;
  node_ids: string[];
}

// ===================== 鉴权相关类型 =====================

export type UserRole = 'admin' | 'teacher' | 'student';

export interface AuthUser {
  id: number;
  username: string;
  displayName?: string;
  display_name?: string;
  email?: string | null;
  role: UserRole;
  isActive?: boolean;
  is_active?: boolean;
  lastLoginAt?: string | null;
  last_login_at?: string | null;
  createdAt?: string;
}

export interface TokenResponse {
  // 注意：响应经适配层 convertEntity 统一转为 camelCase，
  // 因此这里描述的是「页面拿到的形态」，而非后端原始 JSON。
  accessToken: string;
  tokenType: string;
  expiresIn: number;
  user: AuthUser;
}

export interface AuthConfig {
  authRequired: boolean;
  dataIsolation: boolean;
  openRegistration: boolean;
  userCount: number;
  passwordRules: { minLength: number; maxLength: number; hashBackend: string };
}

export interface UserSettings {
  llm_model?: string | null;
  llm_api_base?: string | null;
  default_textbook_version?: string | null;
  default_subject?: string | null;
  default_grade?: string | null;
  default_courseware_id?: number | null;
  default_region?: string | null;
  prefer_ai_review?: boolean | null;
}

/** 令牌与当前用户的本地存储（前端唯一的会话来源） */
export const authStore = {
  TOKEN_KEY: 'token',
  USER_KEY: 'auth_user',

  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  },

  getUser(): AuthUser | null {
    const raw = localStorage.getItem(this.USER_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as AuthUser;
    } catch {
      return null;
    }
  },

  save(token: string, user: AuthUser): void {
    localStorage.setItem(this.TOKEN_KEY, token);
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
  },

  updateUser(user: AuthUser): void {
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
  },

  clear(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
  },

  isLoggedIn(): boolean {
    return !!this.getToken();
  },

  isAdmin(): boolean {
    return (this.getUser()?.role || '').toLowerCase() === 'admin';
  },

  displayName(): string {
    const u = this.getUser();
    if (!u) return '';
    return u.displayName || u.display_name || u.username || '';
  },
};

// ===================== 通用转换工具 =====================

const snakeKey = (key: string): string =>
  key.replace(/([A-Z])/g, (m) => '_' + m.toLowerCase()).replace(/^_/, '');

/** camelCase → snake_case（递归），用于请求参数与载荷 */
export function toSnake<T = any>(input: T): any {
  if (Array.isArray(input)) return input.map((v) => toSnake(v));
  if (input && typeof input === 'object' && !(input instanceof Date) && !(input instanceof File)) {
    const out: Record<string, any> = {};
    Object.entries(input as Record<string, any>).forEach(([k, v]) => {
      if (v === undefined) return;
      out[snakeKey(k)] = toSnake(v);
    });
    return out;
  }
  return input;
}

// 字段别名：后端 snake_case → 前端类型里的名字
// 前端同时保留原始 snake_case 字段，页面用哪个都能取到。
const FIELD_ALIASES: Record<string, string> = {
  question_format: 'type',
  marks: 'score',
  explanation: 'analysis',
  knowledge_points: 'knowledgePoints',
  total_marks: 'totalScore',
  parent_id: 'parentId',
  file_type: 'type',
  file_path: 'fileUrl',
};

const toCamel = (key: string): string => key.replace(/_([a-z])/g, (_, c) => c.toUpperCase());

/** 通用实体转换：camelCase 化 + 关键别名（递归） */
function convertEntity<T = any>(raw: any): T {
  if (Array.isArray(raw)) return raw.map((v) => convertEntity(v)) as unknown as T;
  if (!raw || typeof raw !== 'object' || raw instanceof Blob || raw instanceof Date) return raw;

  const out: Record<string, any> = {};
  Object.entries(raw as Record<string, any>).forEach(([k, v]) => {
    out[toCamel(k)] = Array.isArray(v) || (v && typeof v === 'object') ? convertEntity(v) : v;
  });

  // 别名：注意要读 camelCase 后的键（out 里只有 camelCase 键），
  // 否则会把别名覆写成 undefined。
  Object.entries(FIELD_ALIASES).forEach(([snake, alias]) => {
    const source = out[toCamel(snake)];
    // 实体已明确返回同名字段时保留它（包括 0 / null），例如实际得分 score。
    if (source !== undefined && !Object.prototype.hasOwnProperty.call(out, alias)) out[alias] = source;
  });

  return out as T;
}

/**
 * 判断是否为「列表分页」响应。
 * 后端列表统一返回 {items, total, page, page_size}（无信封）。
 *
 * 必须同时带 `page` 或 `page_size` 才算分页 —— 否则会把**恰好也有 items/total
 * 的业务对象**误当成列表：例如 `/tasks`（items+total+enabled）、
 * `/search/text`（items+total+strategy+query）、`/graph/nodes/{id}/related`
 * 等，一旦被重写成 {list,total}，`enabled` / `strategy` / `recognized_text`
 * 这些字段会被静默丢弃，页面按 .items 取值也会全部拿到空数组。
 */
function isPagedPayload(raw: any): boolean {
  return (
    raw != null &&
    typeof raw === 'object' &&
    !Array.isArray(raw) &&
    Array.isArray((raw as any).items) &&
    typeof (raw as any).total === 'number' &&
    ('page' in raw || 'page_size' in raw)
  );
}

/** 把后端的任意响应包装成页面期望的 {code, message, data} 信封 */
function wrapEnvelope(raw: any): ApiResponse {
  if (isPagedPayload(raw)) {
    return {
      code: 0,
      message: 'ok',
      data: {
        list: convertEntity(raw.items),
        total: raw.total,
        page: raw.page ?? 1,
        pageSize: raw.page_size ?? raw.items.length,
      } as any,
    };
  }

  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    // 知识点树：{nodes: [...]} → 页面期望直接拿数组
    if (Array.isArray(raw.nodes) && Object.keys(raw).length === 1) {
      return { code: 0, message: 'ok', data: convertEntity(raw.nodes) };
    }
    // 学生错题本：{student_id, total, mistakes: [...]} → {list, total}
    if (Array.isArray(raw.mistakes)) {
      return {
        code: 0,
        message: 'ok',
        data: {
          list: convertEntity(raw.mistakes),
          total: raw.total ?? raw.mistakes.length,
          page: raw.page ?? 1,
          pageSize: raw.page_size ?? raw.mistakes.length,
        } as any,
      };
    }
  }

  return { code: 0, message: 'ok', data: convertEntity(raw) };
}

// ===================== axios 实例 =====================

/**
 * 单次 LLM 调用（出题 / 教案 / 讲评 PPT / 课件提取）通常 20~60s，
 * 给到 120s 留足余量；默认的 60s 在模型稍慢时会误报超时。
 */
const DEFAULT_TIMEOUT = 120_000;

/**
 * 多分钟级接口的超时（600s）：
 *   - POST /agents/generate-multi-step：串行 4 次 LLM
 *   - POST /search/reindex：逐题请求 embedding
 * 与生产 nginx 的 `proxy_read_timeout 600s`、vite 代理的 `proxyTimeout` 对齐，
 * 否则前端会在网关之前先把自己的请求取消掉。
 */
const AI_TIMEOUT = 600_000;

const apiClient: AxiosInstance = axios.create({
  baseURL: '/api',
  timeout: DEFAULT_TIMEOUT,
  headers: { 'Content-Type': 'application/json' },
});

apiClient.interceptors.request.use(
  (config) => {
    if(config.method==='get'&&config.url==='/questions/'&&config.params&&encodeURIComponent(JSON.stringify(config.params)).length>4000){
      config.url='/questions/query';config.method='post';config.data=config.params;config.params=undefined;
    }
    const token = authStore.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    // 二进制下载原样返回（页面直接 new Blob([res])）
    if (response.config.responseType === 'blob') {
      return response.data;
    }
    return wrapEnvelope(response.data);
  },
  async (error) => {
    if (axios.isCancel(error)) return Promise.reject(error);
    if (error.response) {
      const { status } = error.response;
      let data = error.response.data;
      // 下载请求的错误响应也是 Blob，解析后才能显示后端的真实原因。
      if (data instanceof Blob && data.size < 1_048_576) {
        try { data = JSON.parse(await data.text()); } catch { /* 使用状态码提示。 */ }
      }
      // 后端错误信息在 detail 上（FastAPI 约定），兼容 message
      const detail = data?.detail ?? data?.message;
      const text =
        typeof detail === 'string'
          ? detail
          : Array.isArray(detail)
            ? detail.map((d: any) => d.msg ?? JSON.stringify(d)).join('；')
            : undefined;

      // 登录/注册本身的 401/403 是业务结果，不能提示「登录已过期」
      const url: string = error.config?.url || '';
      const isAuthEndpoint = /\/auth\/(login|token|register)$/.test(url);

      if (status === 401) {
        if (isAuthEndpoint) {
          message.error(text || '用户名或密码错误');
        } else {
          message.error(text || '登录已过期，请重新登录');
          authStore.clear();
          // 已经在登录页就不要重复跳转，避免刷新循环
          if (!window.location.pathname.startsWith('/login')) {
            window.location.href = '/login';
          }
        }
      } else if (status === 403) {
        message.error(text || '没有权限执行此操作');
      } else if (status === 404) {
        message.error(text || '请求的资源不存在');
      } else if (status === 429) {
        message.error(text || '操作过于频繁，请稍后重试');
      } else if (status === 503) {
        message.error(text || '依赖的服务不可用（如 LLM / OCR 未配置）');
      } else if (status >= 500) {
        message.error(text || '服务器内部错误');
      } else {
        message.error(text || '请求失败');
      }
    } else if (error.request) {
      message.error('网络异常，请检查网络连接');
    } else {
      message.error('请求配置错误');
    }
    return Promise.reject(error);
  }
);

// ===================== 鉴权 API =====================

export const authApi = {
  /** 公开的鉴权策略（是否需要登录 / 是否开放注册） */
  getConfig: () => apiClient.get<ApiResponse<AuthConfig>>('/auth/config'),

  login: (data: { username: string; password: string }) =>
    apiClient.post<ApiResponse<TokenResponse>>('/auth/login', data),

  register: (data: {
    username: string;
    password: string;
    displayName?: string;
    email?: string;
    role?: UserRole;
  }) =>
    apiClient.post<ApiResponse<TokenResponse>>(
      '/auth/register',
      toSnake({ ...data, display_name: data.displayName })
    ),

  getMe: () => apiClient.get<ApiResponse<AuthUser>>('/auth/me'),

  updateProfile: (data: { displayName?: string; email?: string }) =>
    apiClient.put<ApiResponse<AuthUser>>('/auth/me', toSnake(data)),

  changePassword: (data: { oldPassword: string; newPassword: string }) =>
    apiClient.post<ApiResponse<{ message: string }>>(
      '/auth/change-password',
      toSnake(data)
    ),

  getSettings: () =>
    apiClient.get<ApiResponse<{ settings: UserSettings; effective: UserSettings }>>(
      '/auth/me/settings'
    ),

  updateSettings: (data: UserSettings) =>
    apiClient.put<ApiResponse<{ settings: UserSettings }>>('/auth/me/settings', data),

  // ── 用户管理（仅管理员）──
  listUsers: (params?: { page?: number; pageSize?: number; keyword?: string; role?: string }) => {
    const { pageSize, ...rest } = params || {};
    return apiClient.get<ApiResponse<PageResult<AuthUser>>>('/auth/users', {
      params: { ...rest, page_size: pageSize },
    });
  },

  createUser: (data: {
    username: string;
    password: string;
    displayName?: string;
    email?: string;
    role?: UserRole;
  }) =>
    apiClient.post<ApiResponse<AuthUser>>(
      '/auth/users',
      toSnake({ ...data, display_name: data.displayName })
    ),

  updateUser: (
    userId: number | string,
    data: {
      role?: UserRole;
      isActive?: boolean;
      password?: string;
      displayName?: string;
      email?: string;
    }
  ) =>
    apiClient.put<ApiResponse<AuthUser>>(
      `/auth/users/${userId}`,
      toSnake({ ...data, display_name: data.displayName })
    ),

  deleteUser: (userId: number | string) => apiClient.delete(`/auth/users/${userId}`),

  // ── Octop 账号同步（仅管理员）──
  /** Octop 连通状态：地址 / 是否可达 / 是否开启建号自动同步 */
  octopStatus: () =>
    apiClient.get<
      ApiResponse<{
        baseUrl?: string;
        base_url?: string;
        reachable: boolean;
        syncEnabled?: boolean;
        sync_enabled?: boolean;
      }>
    >('/auth/octop-status'),

  /** 手动补同步（存量用户没有明文密码，只能在此显式提供一次） */
  syncOctop: (data: { username: string; password: string }) =>
    apiClient.post<ApiResponse<{ synced?: boolean; error?: string }>>('/auth/sync-octop', data),
};

// ===================== 年级 / 科目字典 API =====================

export interface Curriculum {
  /** 学段分组，如 { group: "高中", grades: ["高一","高二","高三"] } */
  gradeGroups: { group: string; grades: string[] }[];
  /** 全部标准年级（按学段展开） */
  grades: string[];
  /** 各学段的科目 */
  subjectsByGroup: Record<string, string[]>;
  /** 去重后的全部科目 */
  subjects: string[];
  textbookVersions: string[];
}

export const curriculumApi = {
  /** 一次性拉取完整字典，供下拉框 / 筛选器使用 */
  get: () => apiClient.get<ApiResponse<Curriculum>>('/curriculum'),

  grades: (group?: string) =>
    apiClient.get<ApiResponse<{ group?: string; grades?: string[]; gradeGroups?: { group: string; grades: string[] }[] }>>(
      '/curriculum/grades',
      { params: { group } }
    ),

  subjects: (group?: string) =>
    apiClient.get<ApiResponse<{ group?: string; subjects?: string[]; subjectsByGroup?: Record<string, string[]> }>>(
      '/curriculum/subjects',
      { params: { group } }
    ),

  textbookVersions: () => apiClient.get<ApiResponse<{ textbookVersions: string[] }>>('/curriculum/textbook-versions'),

  /** 预览某个年级写法会被归一成什么（导入前校验很有用） */
  normalizeGrade: (grade: string) =>
    apiClient.post<ApiResponse<{ rawGrade: string; grade?: string; group?: string; recognized: boolean }>>(
      '/curriculum/normalize',
      { grade }
    ),
};

// ===================== 题目 API =====================

export const questionApi = {
  getList: (params: {
    page?: number;
    pageSize?: number;
    type?: string;
    difficulty?: string;
    subject?: string;
    /** 年级，支持别名（"7年级" 会被后端归一成 "初一"） */
    grade?: string;
    /** 学段：小学 / 初中 / 高中，一次命中该学段全部年级 */
    gradeGroup?: string;
    /** 来源：manual / uploaded / open_dataset / courseware / ai_generated */
    source?: string;
    status?: 'draft' | 'published';
    textbookVersion?: string;
    region?: string;
    year?: number;
    qualityMin?: number;
    sort?: 'created_desc' | 'quality_desc' | 'difficulty_desc';
    keyword?: string;
    knowledgePoints?: string[];
  }) => {
    const { pageSize, type, knowledgePoints, gradeGroup, textbookVersion, qualityMin, ...rest } = params || {};
    return apiClient.get<ApiResponse<PageResult<Question>>>('/questions/', {
      params: {
        ...rest,
        page_size: pageSize,
        question_format: type,
        grade_group: gradeGroup,
        textbook_version: textbookVersion,
        quality_min: qualityMin,
        knowledge_point_ids:
          knowledgePoints && knowledgePoints.length ? knowledgePoints.join(',') : undefined,
      },
    });
  },

  /** 批量补标年级（历史题目缺 grade 时用；grade 传 null 表示清空归为待分类） */
  bulkGrade: (data: { questionIds: number[]; grade?: string | null }) =>
    apiClient.post<ApiResponse<{ updated: number; grade?: string | null; group?: string | null }>>(
      '/questions/bulk-grade',
      { question_ids: data.questionIds, grade: data.grade ?? null }
    ),

  getById: (id: string) => apiClient.get<ApiResponse<Question>>(`/questions/${id}`),

  create: (data: Partial<Question>) => {
    const payload=toSnake(data);
    const format=({single:'single_choice',multiple:'multi_choice',fill:'fill_blank'} as Record<string,string>)[data.type||'']||data.type||data.questionFormat||'single_choice';
    payload.question_format=format;payload.question_type=['single_choice','multi_choice','true_false'].includes(format)?'objective':'subjective';
    payload.marks=data.score??data.marks??1;payload.explanation=data.analysis??data.explanation??'';
    payload.cognitive_level=data.cognitiveLevel||'comprehension';payload.created_by=authStore.getUser()?.id||1;
    delete payload.type;delete payload.score;delete payload.analysis;
    return apiClient.post<ApiResponse<Question>>('/questions/',payload);
  },

  update: (id: string, data: Partial<Question>) => {
    const payload = toSnake(data);
    const format = data.type ?? data.questionFormat;
    if (format !== undefined) {
      payload.question_format = ({single:'single_choice',multiple:'multi_choice',fill:'fill_blank'} as Record<string,string>)[format] || format;
      payload.question_type = ['single_choice','multi_choice','true_false'].includes(payload.question_format) ? 'objective' : 'subjective';
    }
    if (data.score !== undefined) payload.marks = data.score;
    if (data.analysis !== undefined) payload.explanation = data.analysis;
    delete payload.type; delete payload.score; delete payload.analysis;
    return apiClient.put<ApiResponse<Question>>(`/questions/${id}`, payload);
  },

  delete: (id: string) => apiClient.delete<ApiResponse>(`/questions/${id}`),

  // 后端统一批量入口：POST /questions/batch {question_ids, action}
  batchDelete: (ids: string[]) =>
    apiClient.post<ApiResponse>('/questions/batch', {
      question_ids: ids.map((i) => Number(i)).filter((n) => !Number.isNaN(n)),
      action: 'delete',
    }),

  batchUpdate: (ids: string[], data: Partial<Question>) =>
    apiClient.post<ApiResponse>('/questions/batch', {
      question_ids: ids.map((i) => Number(i)).filter((n) => !Number.isNaN(n)),
      // 后端 batch 仅支持 publish/archive/delete；用 status 字段映射
      action: (data as any)?.status === 'archived' ? 'archive' : 'publish',
    }),

  /**
   * 批量发布题目。
   *
   * 存在的意义：组卷只从 `status=published` 的题里抽题，而 AI 出题与课件出题
   * 落库时都是 `draft`。没有这个入口，AI 生成的题**永远进不了组卷候选池**。
   */
  batchPublish: (ids: Array<string | number>) =>
    apiClient.post<ApiResponse<{ affected_count: number; action: string }>>('/questions/batch', {
      question_ids: ids.map((i) => Number(i)).filter((n) => !Number.isNaN(n)),
      action: 'publish',
    }),

  import: (file: File, options?: { onProgress?: (percent: number) => void }) => {
    const formData = new FormData();
    formData.append('file', file);
    // 后端 /questions/import 需要完整题目对象数组（JSON body），Excel 请走 /import/excel
    const config: AxiosRequestConfig = {
      headers: { 'Content-Type': 'multipart/form-data' },
    };
    if (options?.onProgress) {
      config.onUploadProgress = (e) => {
        if (e.total) options.onProgress!(Math.round((e.loaded * 100) / e.total));
      };
    }
    return apiClient.post<ApiResponse<{ success_count: number; fail_count: number; duplicated_count: number; duplicates: any[] }>>(
      '/import/excel',
      formData,
      config
    );
  },

  getSimilar: (id: string, count?: number) =>
    apiClient.get<ApiResponse<Question[]>>(`/questions/${id}/similar`, {
      params: { top_k: count ?? 10 },
    }),

  // 题目来源分布统计（manual/uploaded/open_dataset/courseware/ai_generated）
  getSources: () => apiClient.get<ApiResponse<QuestionSourceStats>>('/questions/sources'),

  getFacets: () => apiClient.get<ApiResponse<QuestionFacets>>('/questions/facets'),

  // 入库前查重预览（只检查不写入）
  dedupCheck: (contents: string[], subject?: string) =>
    apiClient.post<ApiResponse<DedupCheckResult>>('/import/dedup-check', {
      contents,
      subject,
    }),

  // 导入前解析文件 + 查重预览（不入库），确认后再调 import 真正导入
  previewImport: (file: File, subject?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    if (subject) formData.append('subject', subject);
    return apiClient.post<ApiResponse<DedupCheckResult>>('/import/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// ===================== 试卷 API =====================

/** 学科英文代码 → 中文（页面用 'math'，后端用中文学科名） */
const SUBJECT_LABELS: Record<string, string> = {
  math: '数学',
  chinese: '语文',
  english: '英语',
  physics: '物理',
  chemistry: '化学',
  biology: '生物',
  history: '历史',
  geography: '地理',
  politics: '政治',
};

const toSubjectLabel = (v?: string): string => {
  if (!v) return '数学';
  return SUBJECT_LABELS[v.toLowerCase()] ?? v;
};

/** 把「百分比或小数」的分布统一成后端要求的小数占比（合计归一化） */
function normalizeDistribution(
  dist: Record<string, number> | undefined,
  fallback: Record<string, number>
): Record<string, number> {
  if (!dist || !Object.keys(dist).length) return fallback;
  const values = Object.values(dist).filter((v) => typeof v === 'number' && v > 0);
  if (!values.length) return fallback;
  const sum = values.reduce((a, b) => a + b, 0);
  // 传入的是百分数（如 30/50/20）时 sum>=10，需要除以 100
  const scale = sum > 1.5 ? 100 : 1;
  const out: Record<string, number> = {};
  Object.entries(dist).forEach(([k, v]) => {
    out[k] = Math.round(((Number(v) || 0) / scale) * 1000) / 1000;
  });
  return out;
}

export interface PaperListFilters {
  keyword?: string; paperId?: number; subject?: string; grade?: string; gradeGroup?: string;
  textbookVersion?: string; status?: string; source?: string; createdBy?: number; publishedBy?: number;
  createdFrom?: string; createdTo?: string; publishedFrom?: string; publishedTo?: string;
  minMarks?: number; maxMarks?: number; minDuration?: number; maxDuration?: number;
  minQuestions?: number; maxQuestions?: number; sort?: string;
}

export const paperApi = {
  getList: (params: PaperListFilters & {page?: number; pageSize?: number}) =>
    apiClient.get<ApiResponse<PageResult<Paper>>>('/papers/', {params: toSnake(params || {})}),
  getFilterOptions: () => apiClient.get<ApiResponse<any>>('/papers/filter-options'),

  getById: (id: string) => apiClient.get<ApiResponse<Paper>>(`/papers/${id}`),

  publish: (id: string, expectedHash: string, confirmQuestions: boolean) =>
    apiClient.post<ApiResponse<Paper>>(`/papers/${id}/publish`, {expected_hash: expectedHash, confirm_questions: confirmQuestions}),

  create: (data: Partial<Paper> & { createdBy?: number }) =>
    apiClient.post<ApiResponse<Paper>>('/papers/', {
      title: data.title,
      subject: data.subject,
      grade: data.grade || null,
      total_marks: Number(data.totalScore ?? 100),
      duration: Number(data.duration ?? 60),
      constraints: data.constraints,
      created_by: data.createdBy ?? 1,
    }),

  update: (id: string, data: Partial<Paper>) =>
    apiClient.put<ApiResponse<Paper>>(`/papers/${id}`, toSnake(data)),

  delete: (id: string) => apiClient.delete<ApiResponse>(`/papers/${id}`),

  /**
   * 智能组卷。
   * 页面传的是 UI 结构（difficultyDistribution 用百分数、sections、英文 subject），
   * 后端要求 7 个必填约束字段，这里做完整映射与默认值填充。
   */
  compose: (data: {
    title: string;
    subject?: string;
    grade?: string;
    duration?: number;
    totalScore?: number;
    textbookVersion?: string;
    coursewareId?: string | number;
    sections?: Array<{ name?: string; type?: string; count?: number; scorePerQuestion?: number; difficulty?: string }>;
    difficultyDistribution?: Record<string, number>;
    knowledgePoints?: Array<string | number>;
    constraints?: any;
    edupaperBankRatio?: number;
    octopExpertRatio?: number;
    createdBy?: number;
  }) => {
    const c = data.constraints || {};
    const sections: Array<{ name?: string; type?: string; count?: number; scorePerQuestion?: number; difficulty?: string }> =
      data.sections || c.sections || [];
    const exactSections = sections.map(s => ({
      name:s.name || '',
      question_format:({single:'single_choice',multiple:'multi_choice',fill:'fill_blank'} as Record<string,string>)[s.type || ''] || s.type || 'single_choice',
      count:Number(s.count), marks:Number(s.scorePerQuestion),
      difficulty:s.difficulty && s.difficulty !== 'auto' ? s.difficulty : undefined,
    }));

    // 题型分布：由 sections 汇总，缺失时默认全单选
    let formatDistribution: Record<string, number> = c.formatDistribution || c.format_distribution;
    if (!formatDistribution && sections.length) {
      const total = exactSections.reduce((sum, s) => sum + s.count*s.marks, 0) || 1;
      formatDistribution = {};
      exactSections.forEach(s => {
        formatDistribution![s.question_format] = (formatDistribution![s.question_format] || 0) + s.count*s.marks/total;
      });
    }

    // 知识点节点使用 UUID，不能 Number() 后过滤掉；否则页面明明已选择
    // 知识点，接口仍会收到空数组。
    const knowledgeCoverage = (data.knowledgePoints || c.knowledgePoints || c.knowledge_coverage || [])
      .map((k: any) => String(k).trim())
      .filter(Boolean);
    const coursewareId = data.coursewareId ?? c.coursewareId ?? c.courseware_id;

    return runDurableJob(
      'compose_guided',
      {
        title: data.title,
        created_by: data.createdBy ?? 1,
        // 开启 AI 补题：题库里分值达标的题普遍不足，关掉补题时「100 分的卷子」
        // 实际只会出到 20 多分，而老师看不出差别。缺口由后端按目标分值一次性补。
        auto_fill: true,
        constraints: {
          subject: toSubjectLabel(data.subject || c.subject),
          courseware_id: coursewareId === undefined || coursewareId === null || coursewareId === ''
            ? undefined
            : Number(coursewareId),
          textbook_version: data.textbookVersion || c.textbookVersion || c.textbook_version || '人教版',
          grade: data.grade || c.grade || null,
          total_marks: Number(data.totalScore ?? c.totalScore ?? c.total_marks ?? 100),
          sections:exactSections,
          max_questions:exactSections.length ? exactSections.reduce((sum,s)=>sum+s.count,0) : undefined,
          duration: Number(data.duration ?? c.duration ?? 60),
          difficulty_distribution: normalizeDistribution(
            data.difficultyDistribution || c.difficultyDistribution || c.difficulty_distribution,
            { easy: 0.4, medium: 0.4, hard: 0.2 }
          ),
          format_distribution: formatDistribution || c.formatDistribution || c.format_distribution || { single_choice: 1.0 },
          knowledge_coverage: knowledgeCoverage,
          cognitive_distribution: c.cognitiveDistribution || c.cognitive_distribution,
          region: c.region,
          year_range: c.yearRange || c.year_range,
          dedup_threshold: c.dedupThreshold ?? c.dedup_threshold ?? 0.85,
          exclude_questions: c.excludeQuestions || c.exclude_questions || [],
          edupaper_bank_ratio: Number(data.edupaperBankRatio ?? c.edupaperBankRatio ?? c.edupaper_bank_ratio ?? 50),
          octop_expert_ratio: Number(data.octopExpertRatio ?? c.octopExpertRatio ?? c.octop_expert_ratio ?? 50),
        },
      },
    );
  },

  // 后端：PUT /papers/{paper_id}/questions/{question_id}/replace
  replaceQuestion: (paperId: string, oldQuestionId: string, newQuestionId: string) =>
    apiClient.put<ApiResponse<Paper>>(
      `/papers/${paperId}/questions/${oldQuestionId}/replace`,
      { new_question_id: Number(newQuestionId) }
    ),

  // 手动选题组卷：PUT /papers/{id}/questions
  setQuestions: (
    id: string,
    questions: Array<{ questionId: string | number; order?: number; marks?: number }>
  ) =>
    apiClient.put<ApiResponse<Paper>>(`/papers/${id}/questions`, {
      questions: questions.map((q, i) => ({
        question_id: Number(q.questionId),
        order: q.order ?? i + 1,
        marks: q.marks,
      })),
    }),

  getSpecification: (id: string) =>
    apiClient.get<ApiResponse>(`/papers/${id}/specification`),

  getAnalysis: (id: string) => apiClient.get<ApiResponse>(`/papers/${id}/analysis`),

  getQuality: (id: string) => apiClient.get<ApiResponse>(`/papers/${id}/quality`),

  generateParallel: (id: string) =>
    apiClient.post<ApiResponse<Paper>>(`/papers/${id}/parallel`, { created_by: 1 }),

  /**
   * 双向细目表组卷：按「知识点 × 认知层级 × 分值」矩阵出卷。
   */
  composeBySpec: (data: {
    title: string;
    subject?: string;
    grade?: string;
    textbookVersion?: string;
    coursewareId?: string | number;
    duration?: number;
    edupaperBankRatio?: number;
    octopExpertRatio?: number;
    spec: Array<{
      knowledgePointId: string;
      knowledgePointName?: string;
      knowledge: number;
      comprehension: number;
      application: number;
    }>;
  }) =>
    runDurableJob('compose_spec',
      {
        title: data.title,
        subject: data.subject || '数学',
        grade: data.grade,
        textbook_version: data.textbookVersion || '人教版',
        courseware_id: data.coursewareId ? Number(data.coursewareId) : undefined,
        duration: data.duration ?? 90,
        spec: data.spec.map((r) => ({
          knowledge_point_id: r.knowledgePointId,
          knowledge_point_name: r.knowledgePointName || '',
          knowledge: r.knowledge || 0,
          comprehension: r.comprehension || 0,
          application: r.application || 0,
        })),
        created_by: 1,
        auto_fill: true,
        edupaper_bank_ratio: Number(data.edupaperBankRatio ?? 50),
        octop_expert_ratio: Number(data.octopExpertRatio ?? 50),
      },
    ),
};

// ===================== 知识点 API =====================

export const knowledgeApi = {
  getTree: (subject?: string, coursewareId?: string | number, scope?:{textbookId?:number;grade?:string;textbookVersion?:string;independent?:boolean}) =>
    apiClient.get<ApiResponse<KnowledgeNode[]>>('/knowledge/tree', {
      params: { subject, courseware_id: coursewareId, textbook_id:scope?.textbookId,grade:scope?.grade,textbook_version:scope?.textbookVersion,independent:scope?.independent },
    }),

  // 后端节点 CRUD 都在 /knowledge/nodes 下
  createNode: (data: Partial<KnowledgeNode>) =>
    apiClient.post<ApiResponse<KnowledgeNode>>(
      '/knowledge/nodes',
      toSnake({ subject: '', textbook_version: '通用', ...data })
    ),

  updateNode: (id: string, data: Partial<KnowledgeNode>) =>
    apiClient.put<ApiResponse<KnowledgeNode>>(`/knowledge/nodes/${id}`, toSnake(data)),

  deleteNode: (id: string) => apiClient.delete<ApiResponse>(`/knowledge/nodes/${id}`),

  import: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post<ApiResponse>('/knowledge/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  getNodeQuestions: (id: string, params?: { page?: number; pageSize?: number }) => {
    const { pageSize, ...rest } = params || {};
    return apiClient.get<ApiResponse<PageResult<Question>>>(`/knowledge/nodes/${id}/questions`, {
      params: { ...rest, page_size: pageSize },
    });
  },

  // 从课件提取知识点建议树（仅预览，不入库）。
  // 后端是分段提取（最多 6 段 = 最多 6 次串行 LLM 调用），必须用 AI_TIMEOUT：
  // 默认的 120s 会让整本教材的提取在前端先被取消掉。
  extractFromCourseware: (coursewareId: number) =>
    apiClient.post<ApiResponse<KnowledgeExtraction>>(
      '/knowledge/extract-from-courseware',
      { courseware_id: coursewareId },
      { timeout: AI_TIMEOUT }
    ),

  // 确认并入库知识点树（教师校对后提交）
  applyExtraction: (coursewareId: number, tree: any[]) =>
    apiClient.post<ApiResponse<ApplyExtractionResult>>('/knowledge/apply-extraction', {
      courseware_id: coursewareId,
      tree,
    }),

  // ── 历史孤儿知识点修复 ──────────────────────────────────

  /** 快速看有多少题的知识点对不上现行知识点树 */
  orphanSummary: (subject?: string) =>
    apiClient.get<
      ApiResponse<{
        total_orphans: number;
        /** 有知识点值但都不在现行树里（多为历史 int），可走 plan→apply 映射修复 */
        stale_count?: number;
        /** 完全未标注，没有旧值可映射，只能重新出题 / 导入 / 手工补 */
        untagged_count?: number;
        sample: any[];
      }>
    >('/knowledge/orphans/summary', { params: { subject } }),

  /** 让 AI 建议归类（只诊断不写库） */
  orphanPlan: (params?: { subject?: string; limit?: number }) =>
    apiClient.post<ApiResponse<any>>('/knowledge/orphans/plan', {
      subject: params?.subject,
      limit: params?.limit ?? 50,
    }),

  /** 应用人工校对后的归类结果 */
  orphanApply: (assignments: Array<{ question_id: number; knowledge_point_id?: string | null }>) =>
    apiClient.post<ApiResponse<any>>('/knowledge/orphans/apply', { assignments }),
};

// ===================== 导出 API =====================

// 后端导出为 POST + JSON body：{format: pdf|docx|json, paper_type: teacher|student|...}
const exportPaperFile = (
  paperId: string,
  format: 'pdf' | 'word' | 'docx' | 'json',
  paperType: 'teacher' | 'student' | 'answer_only' | 'exam_points_only',
  includeAnalysis = true,
  showMarks = true
) =>
  apiClient.post<Blob>(
    `/export/paper/${paperId}`,
    {
      format: format === 'word' ? 'docx' : format,
      paper_type: paperType,
      include_analysis: includeAnalysis,
      show_marks: showMarks,
    },
    { responseType: 'blob' }
  );

export const exportApi = {
  exportPaper: (
    id: string,
    options: {
      format: 'pdf' | 'word' | 'json';
      type: 'teacher' | 'student' | 'answer_only' | 'exam_points_only';
      includeAnalysis?: boolean;
      showMarks?: boolean;
    }
  ) =>
    exportPaperFile(
      id,
      options.format,
      options.type,
      options.includeAnalysis ?? true,
      options.showMarks ?? true
    ),

  exportAnswerSheet: (id: string, format: 'pdf' | 'word' = 'pdf') =>
    apiClient.post<Blob>(
      `/export/answer-sheet/${id}`,
      { format: format === 'word' ? 'docx' : format },
      { responseType: 'blob' }
    ),

  exportAnalysis: (id: string, format: 'pdf' | 'word' = 'pdf') =>
    apiClient.post<Blob>(
      `/export/analysis/${id}`,
      { format: format === 'word' ? 'docx' : format, include_knowledge_points: true },
      { responseType: 'blob' }
    ),
};

// ===================== 课件 API =====================

export const coursewareApi = {
  getList: (params: { page?: number; pageSize?: number; subject?: string; grade?: string; textbookVersion?: string; status?: string; keyword?: string }) => {
    const { pageSize, ...rest } = params || {};
    return apiClient.get<ApiResponse<PageResult<Courseware>>>('/courseware/', {
      params: { ...toSnake(rest), page_size: pageSize },
    });
  },

  getById: (id: string) => apiClient.get<ApiResponse<Courseware>>(`/courseware/${id}`),

  // 后端要求 multipart 表单：title/subject/grade/textbook_version/chapter/created_by/file
  upload: async (
    file: File,
    metadata: { subject?: string; grade?: string; type?: string; title?: string; chapter?: string; textbookVersion?: string; autoExtract?: boolean }
  ) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', metadata.title || file.name);
    formData.append('subject', metadata.subject || '未分类');
    if (metadata.grade) formData.append('grade', metadata.grade);
    if (metadata.textbookVersion) formData.append('textbook_version', metadata.textbookVersion);
    if (metadata.chapter) formData.append('chapter', metadata.chapter);
    formData.append('auto_extract', String(metadata.autoExtract ?? true));
    formData.append('created_by', '1');
    const response = await apiClient.post<ApiResponse<Courseware>>('/courseware/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    window.dispatchEvent(new Event('edupaper:coursewares-changed'));
    return response;
  },

  updateMetadata: async (id: string | number, data: { title?: string; subject?: string; grade?: string | null; chapter?: string; textbookVersion?: string; expectedHash?: string }) => {
    const response = await apiClient.patch<ApiResponse<Courseware>>(`/courseware/${id}`, toSnake(data));
    window.dispatchEvent(new Event('edupaper:coursewares-changed'));
    return response;
  },

  delete: async (id: string) => {
    const response = await apiClient.delete<ApiResponse>(`/courseware/${id}`);
    window.dispatchEvent(new Event('edupaper:coursewares-changed'));
    return response;
  },
  /** 重跑正文抽取。用于 .edupdf 教材「先上传、后配置解密口令」的补救场景。 */
  reparse: async (id: string | number) => {
    const response = await apiClient.post<ApiResponse<Courseware>>(`/courseware/${id}/reparse`);
    window.dispatchEvent(new Event('edupaper:coursewares-changed'));
    return response;
  },

  // 后端无独立下载端点：打开公开静态路径由后端 file_path 提供
  download: (id: string) =>
    apiClient.get<Blob>(`/courseware/${id}/download`, { responseType: 'blob' }),

  /**
   * 内联预览课件原文件。
   *
   * 为什么取 blob 而不是直接把 URL 塞进 iframe：预览接口需要 `Authorization`
   * 头，而 iframe / 新窗口发不出这个头，会直接 401。取回二进制后用 object URL
   * 渲染，鉴权仍然走 axios 拦截器。
   */
  preview: (id: string | number) =>
    apiClient.get<Blob>(`/courseware/${id}/preview`, { responseType: 'blob' }),
};

// ===================== 教案 API =====================

export const lessonPlanApi = {
  generate: (data: {
    subject: string;
    grade: string;
    chapter?: string;
    topic?: string;
    knowledgePoints?: Array<string | number>;
    textbookVersion?: string;
    duration?: number;
    coursewareId?: string | number;
    teachingStyle?: string;
    difficultyLevel?: string;
    periodCount?: number;
    extraRequirements?: string;
    createdBy?: number;
  }) =>
    apiClient.post<ApiResponse<LessonPlan>>('/lesson-plans/generate', {
      subject: data.subject,
      grade: data.grade,
      chapter: data.chapter || data.topic || '',
      // 教案专家既支持知识点树 ID，也支持教师在页面输入的知识点名称。
      knowledge_points: data.knowledgePoints || [],
      textbook_version: data.textbookVersion || '人教版',
      duration: data.duration ?? 40,
      courseware_id: data.coursewareId ? Number(data.coursewareId) : undefined,
      teaching_style: data.teachingStyle,
      difficulty_level: data.difficultyLevel,
      period_count: data.periodCount ?? 1,
      extra_requirements: data.extraRequirements,
      created_by: data.createdBy ?? 1,
    }),

  /** 创建后台任务，接口立即返回；生成仍会在页面离开后继续。 */
  startGeneration: (data: {
    subject: string;
    grade: string;
    chapter?: string;
    topic?: string;
    knowledgePoints?: Array<string | number>;
    textbookVersion?: string;
    duration?: number;
    coursewareId?: string | number;
    teachingStyle?: string;
    difficultyLevel?: string;
    periodCount?: number;
    extraRequirements?: string;
    createdBy?: number;
  }) =>
    apiClient.post<ApiResponse<LessonPlanGenerationTask>>('/lesson-plans/generate-tasks', {
      subject: data.subject,
      grade: data.grade,
      chapter: data.chapter || data.topic || '',
      knowledge_points: data.knowledgePoints || [],
      textbook_version: data.textbookVersion || '人教版',
      duration: data.duration ?? 40,
      courseware_id: data.coursewareId ? Number(data.coursewareId) : undefined,
      teaching_style: data.teachingStyle,
      difficulty_level: data.difficultyLevel,
      period_count: data.periodCount ?? 1,
      extra_requirements: data.extraRequirements,
      created_by: data.createdBy ?? 1,
    }),

  getGenerationTask: (taskId: string) =>
    apiClient.get<ApiResponse<LessonPlanGenerationTask>>(`/lesson-plans/generate-tasks/${taskId}`),

  getList: (params: { page?: number; pageSize?: number; subject?: string }) => {
    const { pageSize, ...rest } = params || {};
    return apiClient.get<ApiResponse<PageResult<LessonPlan>>>('/lesson-plans/', {
      params: { ...rest, page_size: pageSize },
    });
  },

  getById: (id: string) => apiClient.get<ApiResponse<LessonPlan>>(`/lesson-plans/${id}`),

  update: (id: string, data: Partial<LessonPlan>) =>
    apiClient.put<ApiResponse<LessonPlan>>(`/lesson-plans/${id}`, toSnake(data)),

  delete: (id: string) => apiClient.delete<ApiResponse>(`/lesson-plans/${id}`),

  export: (id: string, format: 'pdf' | 'pptx' = 'pdf') =>
    apiClient.post<Blob>(
      `/lesson-plans/${id}/export`,
      { format },
      { responseType: 'blob' }
    ),
};

// ===================== 讲评 PPT API =====================

export const reviewPptApi = {
  // 后端：POST /papers/{paper_id}/review-ppt（生成并缓存）
  // 后端仅支持 include_overview；这里兼容页面沿用的 options 参数
  generate: (data: {
    paperId: string | number;
    includeOverview?: boolean;
    options?: { includeOverview?: boolean; [k: string]: any };
  }) =>
    apiClient.post<
      ApiResponse<{ paper_id: number; title: string; slide_count: number; file_name: string }>
    >(`/papers/${data.paperId}/review-ppt`, {
      include_overview: data.includeOverview ?? data.options?.includeOverview ?? true,
    }),

  // 后端：GET /papers/{paper_id}/review-ppt（下载）
  download: (paperId: string | number) =>
    apiClient.get<Blob>(`/papers/${paperId}/review-ppt`, { responseType: 'blob' }),
};

// ===================== 错题 / 答题数据 API =====================

export const mistakeApi = {
  // 后端：POST /exam-records
  createRecord: (data: {
    paperId: string | number;
    studentId: string | number;
    questionId: string | number;
    isCorrect: boolean;
    score: number;
    studentAnswer?: string;
    timeSpent?: number;
  }) => apiClient.post<ApiResponse>('/exam-records', toSnake(data)),

  // 后端：POST /exam-records/batch
  batchCreate: (
    records: Array<{
      paperId: string | number;
      studentId: string | number;
      questionId: string | number;
      isCorrect: boolean;
      score: number;
    }>
  ) => apiClient.post<ApiResponse>('/exam-records/batch', { records: toSnake(records) }),

  /**
   * 错题列表。
   * 传 studentId → 走 /students/{id}/mistakes（学生错题本）
   * 不传      → 走 /mistakes（跨学生列表，用于错题本页面）
   */
  getMistakes: (params: {
    studentId?: string | number;
    page?: number;
    pageSize?: number;
    subject?: string;
  }) => {
    const { studentId, pageSize, ...rest } = params || {};
    const query = { ...rest, page_size: pageSize };
    if (studentId != null && studentId !== '') {
      return apiClient.get<ApiResponse<PageResult<Mistake>>>(`/students/${studentId}/mistakes`, {
        params: query,
      });
    }
    return apiClient.get<ApiResponse<PageResult<Mistake>>>('/mistakes', { params: query });
  },

  // 后端：GET /classes/{id}/mistake-heatmap
  getHeatmap: (params: { classId: string | number; subject?: string }) =>
    apiClient.get<ApiResponse>(`/classes/${params.classId}/mistake-heatmap`, {
      params: { subject: params.subject },
    }),

  /**
   * 自适应推题：按学生对知识点的掌握度动态调整推荐题难度。
   * 不传 knowledgePointIds 时，后端自动用该生掌握度最低的薄弱知识点。
   */
  adaptivePractice: (
    studentId: string | number,
    params?: { knowledgePointIds?: string[]; count?: number }
  ) => {
    const { knowledgePointIds, count } = params || {};
    return apiClient.get<ApiResponse<{ studentId: number; total: number; items: any[] }>>(
      `/students/${studentId}/adaptive-practice`,
      {
        params: {
          knowledge_point_ids: knowledgePointIds?.length
            ? knowledgePointIds.join(',')
            : undefined,
          count: count ?? 10,
        },
      }
    );
  },
};

// ===================== 学情分析 API =====================

export const analyticsApi = {
  classes: () => apiClient.get<ApiResponse<Array<{ id: number; name: string; grade?: string; subject?: string }>>>('/classes'),
  classOverview: (params: { classId: string | number; subject?: string }) =>
    apiClient.get<ApiResponse>(`/classes/${params.classId}/analytics`, {
      params: { subject: params.subject },
    }),

  mastery: (params: { classId: string | number; subject?: string }) =>
    apiClient.get<ApiResponse>(`/classes/${params.classId}/mastery`, {
      params: { subject: params.subject },
    }),

  studentAnalysis: (studentId: string | number) =>
    apiClient.get<ApiResponse>(`/students/${studentId}/analytics`),

  paperQuality: (paperId: string | number) =>
    apiClient.get<ApiResponse>(`/papers/${paperId}/quality`),

  // 后端：POST /classes/{id}/report {format: pdf|excel}
  exportReport: (params: { classId: string | number; format: 'pdf' | 'excel'; paperId?: number }) =>
    apiClient.post<Blob>(
      `/classes/${params.classId}/report`,
      { format: params.format, ...(params.paperId != null ? { paper_id: params.paperId } : {}) },
      { responseType: 'blob' }
    ),
};

// ===================== 学生 API =====================

export interface StudentItem {
  id: number;
  name: string;
  classId?: number | null;
  studentNumber?: string | null;
}

export const studentApi = {
  list: (params?: { classId?: number; keyword?: string }) =>
    apiClient.get<ApiResponse<StudentItem[]>>('/students/', { params }),
  create: (data: { name: string; classId?: number; studentNumber?: string }) =>
    apiClient.post<ApiResponse<StudentItem>>(
      '/students/',
      toSnake({ ...data, class_id: data.classId, student_number: data.studentNumber })
    ),
};

// ===================== OCR API =====================

/** 把 dataURL / base64 字符串转成 File，兼容页面上传组件产出的是图片预览地址的情况 */
function toUploadFile(input: File | string, fallbackName = 'upload.png'): File {
  if (typeof input !== 'string') return input;
  const [meta, base64] = input.split(',');
  const mime = /data:([^;]+);/.exec(meta)?.[1] || 'image/png';
  const ext = mime.split('/')[1] || 'png';
  const binary = atob(base64 ?? '');
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new File([bytes], `${fallbackName}`.replace(/\.\w+$/, `.${ext}`), { type: mime });
}

export const ocrApi = {
  uploadWhole: (files:File[],meta:any)=>{const data=new FormData();files.forEach(file=>data.append('files',file));data.append('subject',meta.subject);data.append('grade',meta.grade);data.append('textbook_version',meta.textbookVersion);if(meta.coursewareId)data.append('courseware_id',String(meta.coursewareId));return apiClient.post<ApiResponse<any>>('/ocr/scan-paper/upload',data,{timeout:AI_TIMEOUT,headers:{'Content-Type':'multipart/form-data'}});},
  getPageImage: (scanId:string|number,pageId:string)=>apiClient.get(`/ocr/scans/${scanId}/pages/${pageId}/image`,{responseType:'blob'}),
  listScans: (params?: { type?: 'paper' | 'answer'; page?: number; pageSize?: number }) =>
    apiClient.get<ApiResponse<PageResult<any>>>('/ocr/scans', { params: { type: params?.type, page: params?.page, page_size: params?.pageSize } }),
  getScanImage: (scanId: string | number) => apiClient.get(`/ocr/scans/${scanId}/image`, {responseType:'blob'}),
  saveDraft: (scanId: string | number, data: {title:string;duration:number;version:number}) => apiClient.post<ApiResponse<any>>(`/ocr/scan-paper/${scanId}/draft`, data),
  confirmPhoto: (paperId: string, version: number, confirmed: boolean) => apiClient.post<ApiResponse<any>>(`/papers/${paperId}/confirm-photo`, {version,confirmed}),
  getScan: (scanId: string | number) => apiClient.get<ApiResponse<any>>(`/ocr/scans/${scanId}`),
  // 后端要求 multipart：file + subject + textbook_version + created_by
  // 入参同时接受 File 与 dataURL 字符串
  scanPaper: (
    file: File | string,
    meta?: { subject?: string; grade?: string; textbookVersion?: string; coursewareId?: string | number }
  ) => {
    const formData = new FormData();
    formData.append('file', toUploadFile(file, 'scan_paper.png'));
    formData.append('subject', meta?.subject || '未分类');
    if (meta?.grade) formData.append('grade', meta.grade);
    formData.append('textbook_version', meta?.textbookVersion || '人教版');
    if(meta?.coursewareId) formData.append('courseware_id', String(meta.coursewareId));
    return apiClient.post<
      ApiResponse<{ scanId: number; status: string; recognizedQuestions: any[]; rawText?: string }>
    >('/ocr/scan-paper', formData, { timeout: AI_TIMEOUT, headers: { 'Content-Type': 'multipart/form-data' } });
  },

  /** 校正：兼容 {scanId, questions} 与页面旧写法 {questions}（需带 scanId） */
  correctOcr: (data: { scanId?: string | number; version?: number; questions: any[] }) => {
    if (data.scanId == null) {
      return Promise.reject(new Error('缺少 scanId：请先完成 OCR 识别'));
    }
    return apiClient.put<ApiResponse>(`/ocr/scan-paper/${data.scanId}`, {
      version: data.version,
      questions: data.questions.map((question) => ({
        ...toSnake(question),
        marks: question.score ?? question.marks ?? 1,
        explanation: question.analysis ?? question.explanation ?? '',
        question_format: ({ single: 'single_choice', multiple: 'multi_choice', fill: 'fill_blank' } as Record<string, string>)[question.type] || question.type || question.questionFormat || question.question_format,
        options: Array.isArray(question.options) ? question.options.map((option: any, index: number) =>
          typeof option === 'string' ? { label: String.fromCharCode(65 + index), text: option } : option) : question.options,
      })),
    });
  },

  /** 导入题库：后端按 scan_id 导入已校正的题目 */
  importOcr: (data: {
    scanId?: string | number;
    questionIds?: number[];
    questions?: any[];
    subject?: string;
    grade?: string;
  }) => {
    if (data.scanId == null) {
      return Promise.reject(new Error('缺少 scanId：请先完成 OCR 识别与校正'));
    }
    return apiClient.post<ApiResponse>(`/ocr/scan-paper/${data.scanId}/import`, {
      question_ids: data.questionIds ?? [],
    });
  },

  // 后端要求 multipart：file + paper_id + student_id
  scanAnswer: (
    file: File | string,
    meta?: { paperId?: string | number; studentId?: string | number }
  ) => {
    const formData = new FormData();
    formData.append('file', toUploadFile(file, 'scan_answer.png'));
    formData.append('paper_id', String(meta?.paperId ?? 1));
    formData.append('student_id', String(meta?.studentId ?? 1));
    return apiClient.post<
      ApiResponse<{ scanId: number; status: string; recognizedAnswers: any[] }>
    >('/ocr/scan-answer', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },

  // 后端：POST /ocr/scan-answer/{scan_id}/grade
  correctAnswers: (data: { scanId: number; version?: number; answers: Array<{ order: number; studentAnswer: string }> }) =>
    apiClient.put<ApiResponse<any>>(`/ocr/scan-answer/${data.scanId}`, toSnake({ version: data.version, answers: data.answers })),
  reviewScan: (data: { scanId: number; version: number; scores: Array<{ order: number; score: number; comment?: string }> }) =>
    apiClient.post<ApiResponse<any>>(`/ocr/scan-answer/${data.scanId}/review`, { version: data.version, scores: data.scores }),
  autoGrade: (data: { scanId?: string | number; paperId?: string | number; studentId?: string | number }) => {
    if (data.scanId == null || data.paperId == null || data.studentId == null) {
      return Promise.reject(new Error('缺少 scanId、paperId 或 studentId：请先上传答题卡'));
    }
    return apiClient.post<ApiResponse>(`/ocr/scan-answer/${data.scanId}/grade`, {
      paper_id: Number(data.paperId),
      student_id: Number(data.studentId),
    });
  },

  /** 按图片 URL 批改（IM / Octop 通道转发过来的图片链接） */
  scanAnswerUrl: (data: {
    imageUrl: string;
    paperId?: string | number;
    studentId?: string | number;
    studentName?: string;
  }) =>
    apiClient.post<ApiResponse<{ scanId: number; status: string; recognizedAnswers: any[] }>>(
      '/ocr/scan-answer-url',
      {
        image_url: data.imageUrl,
        paper_id: data.paperId ?? 1,
        student_id: data.studentId,
        student_name: data.studentName,
      }
    ),

  // 后端：GET /ocr/scan-answer/{scan_id}/results
  getResults: (scanId: string | number) =>
    apiClient.get<ApiResponse>(`/ocr/scan-answer/${scanId}/results`),
};

// ===================== IM 一句话出卷 API =====================

export const weixinApi = {
  status: (signal?: AbortSignal) => apiClient.get<ApiResponse<any>>('/weixin/binding', {signal}),
  start: () => apiClient.post<ApiResponse<any>>('/weixin/binding/qrcode'),
  poll: (signal?: AbortSignal) => apiClient.post<ApiResponse<any>>('/weixin/binding/poll', {}, {timeout: 55_000,signal}),
  pairingCode: () => apiClient.post<ApiResponse<any>>('/weixin/binding/pairing-code'),
  disconnect: () => apiClient.delete<ApiResponse<any>>('/weixin/binding'),
  receipts: (signal?: AbortSignal) => apiClient.get<ApiResponse<any>>('/weixin/receipts', {signal}),
};

export const imApi = {
  /** 一句话自然语言出卷：解析意图 → 组卷 → 返回试卷摘要 */
  compose: (
    text: string,
    autoFill = true,
    edupaperBankRatio = 50,
    octopExpertRatio = 50,
    coursewareId?: string | number,
    teachingScope?: {subject:string;grade:string;textbookVersion:string},
  ) =>
    runDurableJob('compose_prompt',
      {
        text,
        subject:teachingScope?.subject,grade:teachingScope?.grade,textbook_version:teachingScope?.textbookVersion,
        created_by: 1,
        auto_fill: autoFill,
        edupaper_bank_ratio: edupaperBankRatio,
        octop_expert_ratio: octopExpertRatio,
        courseware_id: coursewareId === undefined || coursewareId === null || coursewareId === ''
          ? undefined
          : Number(coursewareId),
      },
    ),
};

// ===================== AI Agent API =====================

/** 请求只负责入队；进度和结果保存在服务端，刷新后可从后台任务打开。 */
export async function runDurableJob(kind: string, payload: any, requestKey?: string): Promise<any> {
  const owner = authStore.getUser()?.id;
  const body = toSnake(payload);
  const signature = JSON.stringify([kind, body]);
  const storageKey = `edupaper:work-job:${owner || 'demo'}:${kind}`;
  let key = requestKey;
  if (!key) {
    try {const previous=JSON.parse(localStorage.getItem(storageKey)||'null');if(previous?.signature===signature)key=previous.key;}catch{}
    key ||= crypto.randomUUID();
    localStorage.setItem(storageKey,JSON.stringify({key,signature}));
  }
  let response: any = await apiClient.post('/jobs', {kind,payload:body,request_key:key});
  message.info('已提交后台任务，可在“后台任务”查看进度和结果');
  const deadline=Date.now()+30*60*1000;
  while (response.data.status==='queued'||response.data.status==='running') {
    if (authStore.getUser()?.id!==owner) throw new Error('账号已切换');
    if (Date.now()>deadline) {message.info('任务仍在后台处理，请在“后台任务”查看');throw new Error('后台处理仍在继续');}
    await new Promise(resolve=>window.setTimeout(resolve,2500));
    response=await apiClient.get(`/jobs/${response.data.id}`);
  }
  if(response.data.status!=='completed') {
    const reason=response.data.error||(response.data.status==='cancelled'?'任务已取消':'任务处理失败');
    message.error(reason);
    if(response.data.status==='cancelled')localStorage.removeItem(storageKey);
    const error=Object.assign(new Error(reason),{jobStatus:response.data.status,jobId:response.data.id});
    throw error;
  }
  localStorage.removeItem(storageKey);
  return {...response,data:response.data.result};
}

export const agentApi = {
  /**
   * AI 出题。
   * 同时兼容两种入参写法：页面沿用的 {type, knowledgePoints[]} 与新的
   * {questionFormat, knowledgePoint}。后端每次只接受一个知识点。
   * 响应额外补出 `questions` 字段（后端原名 `generated`），页面无需改动。
   */
  generate: (data: {
    subject: string;
    coursewareId?: string | number;
    difficulty: string;
    count: number;
    type?: string;
    /** 页面沿用的年级字段（后端出题接口不使用，仅为兼容入参） */
    grade?: string;
    questionFormat?: string;
    knowledgePoint?: string;
    knowledgePoints?: Array<string | number>;
    textbookVersion?: string;
    region?: string;
    references?: number[];
    createdBy?: number;
  }) => {
    const kp =
      data.knowledgePoint ??
      (data.knowledgePoints && data.knowledgePoints.length
        ? String(data.knowledgePoints[0])
        : '');
    return runDurableJob('question_generate', {
        courseware_id: data.coursewareId ? Number(data.coursewareId) : undefined,
        knowledge_point: kp,
        difficulty: data.difficulty,
        question_format: data.questionFormat || data.type || 'single_choice',
        textbook_version: data.textbookVersion || '人教版',
        subject: data.subject,
        // 年级必须传：后端会把题目 grade 落成 null（待分类），
        // 组卷按年级严格筛选时这些题永远命中不到。
        grade: data.grade,
        region: data.region,
        count: data.count,
        references: data.references,
        created_by: data.createdBy ?? 1,
      })
      .then((res: any) => ({
        ...res,
        data: { ...res.data, questions: res.data?.generated ?? [] },
      }));
  },

  /** 按输入顺序逐题校验，保留待复核状态与每题问题。 */
  validate: async (data: {
    questions?: Array<Partial<Question>>;
    questionId?: number;
    content?: string;
    answer?: string;
    explanation?: string;
    options?: any;
  }) => {
    const inputs = data.questions?.length ? data.questions : [data];
    const results = [];
    for (const input of inputs) {
      const question = input as any;
      const res: any = await apiClient.post('/agents/validate', toSnake({
        question_id: /^\d+$/.test(String(question.id ?? question.questionId ?? '')) ? Number(question.id ?? question.questionId) : undefined,
        content: question.content,
        answer: question.answer,
        explanation: question.explanation ?? question.analysis,
        options: question.options,
        question_format: question.questionFormat ?? question.question_format ?? question.type,
        difficulty: question.difficulty,
        knowledge_points: question.knowledgePoints ?? question.knowledge_points,
      }), { timeout: AI_TIMEOUT });
      const d = res.data || {};
      results.push({
        valid: d.overallPassed ?? d.overall_passed ?? false,
        requiresReview: d.requiresReview ?? d.requires_review ?? true,
        issues: d.suggestions || [],
        status: d.status,
      });
    }
    return { data: { results } };
  },

  generateFromCourseware: (data: {
    coursewareId: string | number;
    grade?: string;
    knowledgePoints?: string[];
    count: number;
    difficulty: string;
    questionFormat?: string;
    type?: string;
  }) =>
    apiClient
      .post<ApiResponse<{ generated: Question[]; savedIds: number[]; total: number }>>(
        '/agents/generate-from-courseware',
        {
          courseware_id: Number(data.coursewareId),
          grade: data.grade,
          knowledge_points: data.knowledgePoints || [],
          difficulty: data.difficulty,
          question_format: data.questionFormat || data.type || 'single_choice',
          count: data.count,
          created_by: 1,
        }
      )
      .then((res: any) => ({
        ...res,
        data: { ...res.data, questions: res.data?.generated ?? [] },
      })),

  getStatus: () =>
    apiClient.get<
      ApiResponse<{
        status: string;
        activeTasks: number;
        models: any[];
        queueLength: number;
        uptimeSeconds: number;
      }>
    >('/agents/status'),

  /** 多步出题：规划 → 生成 → 审校 → 修正（比单次调用更稳、可观测） */
  generateMultiStep: (data: {
    coursewareId?: string | number;
    knowledgePoint: string;
    knowledgePointName?: string;
    subject: string;
    grade?: string;
    difficulty: string;
    questionFormat?: string;
    textbookVersion?: string;
    region?: string;
    count: number;
    references?: number[];
    cognitiveLevel?: string;
    enableRevise?: boolean;
    save?: boolean;
  }) =>
    apiClient.post<ApiResponse<any>>('/agents/generate-multi-step', {
      courseware_id: data.coursewareId ? Number(data.coursewareId) : undefined,
      knowledge_point: data.knowledgePoint,
      knowledge_point_name: data.knowledgePointName,
      subject: data.subject,
      grade: data.grade,
      difficulty: data.difficulty,
      question_format: data.questionFormat || 'single_choice',
      textbook_version: data.textbookVersion || '人教版',
      region: data.region,
      count: data.count,
      references: data.references,
      cognitive_level: data.cognitiveLevel,
      enable_revise: data.enableRevise !== false,
      save: data.save !== false,
      created_by: 1,
    }, { timeout: AI_TIMEOUT }),
};

// ===================== 搜题 API =====================

export interface SearchHit {
  /** 题目 ID（后端显式返回，收藏/跳转都用它） */
  id: number;
  questionId?: number;
  content: string;
  answer?: string;
  explanation?: string;
  difficulty?: string;
  questionFormat?: string;
  question_format?: string;
  /** 题目分值；缺少显式 score 时通用适配会将 marks 别名为 score。 */
  marks?: number;
  score?: number;
  /**
   * 混合召回相关度（0~1）。相关度使用此字段，避免与题目分值混淆。
   */
  similarity?: number;
  subject?: string;
  grade?: string;
  knowledgePoints?: any[];
  /** 命中原因：literal / semantic / knowledge_point */
  matchReasons?: string[];
  match_reasons?: string[];
}

export interface SearchResult {
  query?: string;
  total: number;
  items: SearchHit[];
  semantic?: boolean;
  recognizedText?: string;
  recognized_text?: string;
}

export const searchApi = {
  /** 文本搜题（关键词 + 语义混合召回） */
  text: (params: {
    query: string;
    subject?: string;
    grade?: string;
    textbookVersion?: string;
    knowledgePointIds?: string[];
    questionFormat?: string;
    difficulty?: string;
    topK?: number;
    semantic?: boolean;
  }) =>
    apiClient.post<ApiResponse<SearchResult>>('/search/text', {
      query: params.query,
      subject: params.subject,
      grade: params.grade,
      textbook_version: params.textbookVersion,
      knowledge_point_ids: params.knowledgePointIds,
      question_format: params.questionFormat,
      difficulty: params.difficulty,
      top_k: params.topK ?? 20,
      semantic: params.semantic !== false,
    }),

  /** 拍照搜题：先由视觉模型读出题干，再走文本检索 */
  image: (file: File, opts?: { subject?: string; grade?: string; textbookVersion?: string; topK?: number }) => {
    const form = new FormData();
    form.append('file', file);
    if (opts?.subject) form.append('subject', opts.subject);
    if (opts?.grade) form.append('grade', opts.grade);
    if (opts?.textbookVersion) form.append('textbook_version', opts.textbookVersion);
    form.append('top_k', String(opts?.topK ?? 20));
    return apiClient.post<ApiResponse<SearchResult>>('/search/image', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  /** 向量索引状态 */
  indexStatus: () => apiClient.get<ApiResponse<any>>('/search/index'),

  /** 重建向量索引 */
  reindex: (limit?: number) =>
    apiClient.post<ApiResponse<any>>('/search/reindex', { limit }, { timeout: AI_TIMEOUT }),
};

// ===================== 个人题库（收藏 + 题单）API =====================

export interface FavoriteItem {
  id: number;
  questionId?: number;
  question_id?: number;
  note?: string;
  createdAt?: string;
  created_at?: string;
  question?: Question;
}

export interface QuestionCollection {
  id: number;
  name: string;
  description?: string;
  itemCount?: number;
  item_count?: number;
  createdAt?: string;
  created_at?: string;
}

export const collectionApi = {
  listFavorites: (params?: { page?: number; pageSize?: number }) =>
    apiClient.get<ApiResponse<PageResult<FavoriteItem>>>('/collections/favorites', {
      params: { page: params?.page, page_size: params?.pageSize },
    }),

  addFavorite: (questionId: number, note?: string) =>
    apiClient.post<ApiResponse<FavoriteItem>>('/collections/favorites', {
      question_id: questionId,
      note,
    }),

  removeFavorite: (questionId: number) =>
    apiClient.delete<ApiResponse<any>>(`/collections/favorites/${questionId}`),

  /** 收藏题目 ID 集合（用于列表页标记「已收藏」） */
  favoriteIds: () => apiClient.get<ApiResponse<number[]>>('/collections/favorites/ids'),

  listCollections: () => apiClient.get<ApiResponse<QuestionCollection[]>>('/collections'),

  createCollection: (name: string, description?: string) =>
    apiClient.post<ApiResponse<QuestionCollection>>('/collections', { name, description }),

  updateCollection: (id: number, data: { name?: string; description?: string }) =>
    apiClient.put<ApiResponse<QuestionCollection>>(`/collections/${id}`, data),

  deleteCollection: (id: number) =>
    apiClient.delete<ApiResponse<any>>(`/collections/${id}`),

  collectionItems: (id: number) =>
    apiClient.get<ApiResponse<Question[]>>(`/collections/${id}/items`),

  addToCollection: (id: number, questionIds: number[]) =>
    apiClient.post<ApiResponse<any>>(`/collections/${id}/items`, {
      question_ids: questionIds,
    }),

  removeFromCollection: (id: number, questionId: number) =>
    apiClient.delete<ApiResponse<any>>(`/collections/${id}/items/${questionId}`),

  /** 用题单直接组卷 */
  composeFromCollection: (id: number, data?: { title?: string; totalMarks?: number }) =>
    apiClient.post<ApiResponse<any>>(`/collections/${id}/compose`, {
      title: data?.title,
      total_marks: data?.totalMarks,
      created_by: 1,
    }),
};

// ===================== 知识图谱 API =====================

export const graphApi = {
  status: () => apiClient.get<ApiResponse<any>>('/graph/status'),

  /** 把知识点与题目关系同步进 Neo4j */
  sync: () => apiClient.post<ApiResponse<any>>('/graph/sync', {}),

  prerequisites: (nodeId: string, depth = 3) =>
    apiClient.get<ApiResponse<any>>(`/graph/nodes/${nodeId}/prerequisites`, {
      params: { depth },
    }),

  dependents: (nodeId: string, depth = 3) =>
    apiClient.get<ApiResponse<any>>(`/graph/nodes/${nodeId}/dependents`, {
      params: { depth },
    }),

  related: (nodeId: string, limit = 10) =>
    apiClient.get<ApiResponse<any>>(`/graph/nodes/${nodeId}/related`, {
      params: { limit },
    }),

  nodeQuestions: (nodeId: string, limit = 20) =>
    apiClient.get<ApiResponse<any>>(`/graph/nodes/${nodeId}/questions`, {
      params: { limit },
    }),

  learningPath: (fromNodeId: string, toNodeId: string) =>
    apiClient.get<ApiResponse<any>>('/graph/learning-path', {
      // 后端签名是 from / to（Query alias），不是 from_node_id / to_node_id
      params: { from: fromNodeId, to: toNodeId },
    }),
};

// ===================== 定时任务 API =====================

export interface ScheduledTask {
  job_id: string;
  jobId?: string;
  name: string;
  next_run_time?: string;
  nextRunTime?: string;
  interval_minutes?: number;
  intervalMinutes?: number;
}

export interface TaskRun {
  id?: number;
  job_id: string;
  jobId?: string;
  job_name?: string;
  jobName?: string;
  status: string;
  started_at?: string;
  startedAt?: string;
  finished_at?: string;
  finishedAt?: string;
  elapsed_seconds?: number;
  elapsedSeconds?: number;
  detail?: string;
  result?: any;
}

export const taskApi = {
  list: () => apiClient.get<ApiResponse<{ enabled: boolean; total: number; items: ScheduledTask[] }>>('/tasks'),

  runs: (params?: { limit?: number; jobId?: string }) =>
    apiClient.get<ApiResponse<{ total: number; items: TaskRun[] }>>('/tasks/runs', {
      params: { limit: params?.limit ?? 50, job_id: params?.jobId },
    }),

  /** 立刻执行一次（管理员） */
  runNow: (jobId: string) =>
    apiClient.post<ApiResponse<any>>(`/tasks/${jobId}/run`, {}),
};

// ===================== Octop 集成 API =====================

export const octopApi = {
  /** 总览：通道 / 技能 / SSO 三块状态 */
  overview: (agentId?: string) =>
    apiClient.get<ApiResponse<any>>('/octop/overview', { params: { agent_id: agentId } }),

  agentList: () => apiClient.get<ApiResponse<any>>('/octop/agents'),

  channelKinds: () => apiClient.get<ApiResponse<any>>('/octop/channels/kinds'),

  channels: (agentId: string) =>
    apiClient.get<ApiResponse<any>>(`/octop/agents/${agentId}/channels`),

  createChannel: (agentId: string, data: { kind: string; name?: string; config?: any }) =>
    apiClient.post<ApiResponse<any>>(`/octop/agents/${agentId}/channels`, data),

  deleteChannel: (agentId: string, channelId: number) =>
    apiClient.delete<ApiResponse<any>>(`/octop/agents/${agentId}/channels/${channelId}`),

  testChannel: (agentId: string, channelId: number) =>
    apiClient.post<ApiResponse<any>>(`/octop/agents/${agentId}/channels/${channelId}/test`, {}),

  /** 生成扫码绑定二维码（有效期很短，需立即开始轮询） */
  startQrBind: (agentId: string, kind: string) =>
    apiClient.post<ApiResponse<{ qrcode_token: string; qrcode_url: string }>>(
      `/octop/agents/${agentId}/channels/${kind}/bind/qrcode`,
      {}
    ),

  pollQrBind: (agentId: string, kind: string, token: string) =>
    apiClient.post<ApiResponse<any>>(
      `/octop/agents/${agentId}/channels/${kind}/bind/qrcode/poll`,
      { qrcode_token: token }
    ),

  plugins: () => apiClient.get<ApiResponse<any>>('/octop/plugins'),

  reloadPlugins: () => apiClient.post<ApiResponse<any>>('/octop/plugins/reload', {}),

  agentTools: (agentId: string) =>
    apiClient.get<ApiResponse<any>>(`/octop/agents/${agentId}/tools`),

  ssoStatus: () => apiClient.get<ApiResponse<any>>('/octop/sso/status'),

  ssoConfigure: (data?: { enabled?: boolean; issuer?: string; clientId?: string; clientSecret?: string }) =>
    apiClient.post<ApiResponse<any>>('/octop/sso/configure', {
      enabled: data?.enabled !== false,
      issuer: data?.issuer,
      client_id: data?.clientId,
      client_secret: data?.clientSecret,
    }),

  ssoTest: () => apiClient.post<ApiResponse<any>>('/octop/sso/test', {}),

  ssoLinksPlan: () => apiClient.get<ApiResponse<any>>('/octop/sso/links/plan'),

  ssoLinksApply: (usernames?: string[]) =>
    apiClient.post<ApiResponse<any>>('/octop/sso/links/apply', {
      usernames: usernames || [],
    }),
};

export default apiClient;
